import { parseBarcode } from "gs1-barcode-parser-mod";

/** A UPC-A is 12 digits, the last of which is a check digit. */
const UPC_BARCODE_LENGTH = 12;

/**
 * What a scan was recognised as.
 *
 * The order the kinds are tried in is part of the behaviour, not an implementation detail: a bare
 * NDC and a GS1 payload are only told apart by everything else having been ruled out first, so
 * anything unrecognised falls through to GS1 rather than being rejected.
 */
export enum ScannedBarcodeKind {
    /** A CPHub Rx label. */
    Standard = "standard",
    /** A 12 digit UPC-A whose check digit verifies. */
    Upc = "upc",
    /** A bare 10 or 11 digit NDC. */
    Ndc = "ndc",
    /** Anything else, which is handed to the GS1 parser. */
    Gs1 = "gs1",
}

/** A CPHub Rx label: prescription, fill and print ids, each numeric. */
export function isStandardBarcode(barcode: string): boolean {
    return /^[0-9]+-[0-9]+-[0-9]+$/.test(barcode);
}

/** A bare NDC, either 10 or 11 digits. */
export function isNdcBarcode(barcode: string): boolean {
    return /^\d{10,11}$/.test(barcode);
}

/**
 * A 12 digit UPC-A, verified against its own check digit. The check digit is what keeps a 12 digit
 * code that merely looks like a UPC from being treated as one.
 */
export function isUpcBarcode(barcode: string): boolean {
    if (barcode.length !== UPC_BARCODE_LENGTH) return false;

    let sum = 0;
    for (let i = 0; i < 11; i++) {
        sum += parseInt(barcode[i]) * (i % 2 === 0 ? 3 : 1);
    }

    const checkDigit = (10 - (sum % 10)) % 10;
    return checkDigit === parseInt(barcode[11]);
}

/** Which kind of code a scan is, tried in the order described on ScannedBarcodeKind. */
export function classifyBarcode(barcode: string): ScannedBarcodeKind {
    if (isStandardBarcode(barcode)) return ScannedBarcodeKind.Standard;
    if (isUpcBarcode(barcode)) return ScannedBarcodeKind.Upc;
    if (isNdcBarcode(barcode)) return ScannedBarcodeKind.Ndc;
    return ScannedBarcodeKind.Gs1;
}

/** The FNC1 field separator, as it has to appear in the string before the parser will see it. */
export const FNC1 = "\x1D";

/**
 * The key sequences a wedge scanner sends in place of the FNC1 separator.
 *
 * A scanner typing into a field cannot deliver 0x1D as a character, so it sends the Windows alt code
 * for it instead. What the browser then reports depends on NumLock: with it on the numpad sends
 * digits, with it off it sends the navigation keys printed on the same physical keys.
 */
const FNC1_KEY_SEQUENCES = [
    // NumLock on: Alt + 0029, or Alt + 029.
    "Alt0029",
    "Alt029",
    // NumLock off: 0 arrives as Insert, 2 as ArrowDown, 9 as PageUp.
    "AltInsertArrowDownPageUp",
    "AltInsertInsertArrowDownPageUp",
];

/**
 * Watches keystrokes for the alt code a scanner sends in place of the FNC1 separator.
 *
 * Held per input rather than shared, because two scan fields would otherwise interleave their
 * keystrokes into one buffer.
 */
export class Fnc1KeySequence {
    private keys: string[] = [];

    /**
     * Feeds one keydown in. Returns true when the keys so far complete a separator, at which point
     * the caller should insert FNC1 and the buffer is cleared.
     */
    accept(key: string, altKey: boolean): boolean {
        if (!altKey) return false;

        this.keys.push(key);

        if (!FNC1_KEY_SEQUENCES.includes(this.keys.join(""))) return false;

        this.keys = [];
        return true;
    }

    /** Drops a part-typed sequence, so a fresh scan does not inherit it. */
    reset(): void {
        this.keys = [];
    }
}

/**
 * The application identifiers a scanned drug package carries. Absent fields are left undefined so
 * the shape drops straight into the optional properties callers hold them in.
 */
export interface Gs1ScanFields {
    /** AI 01. */
    gtin?: string;
    /** AI 10. */
    lotNumber?: string;
    /** AI 21. */
    serialNumber?: string;
    /** AI 17, with a day of "00" already resolved to the last day of that month. */
    expirationDate?: Date;
}

/**
 * Whether a parsed AI 17 is a date the barcode could actually have encoded.
 *
 * The parser builds its Date by arithmetic rather than validating first, so an impossible yyMMdd is
 * not rejected — it overflows into a real looking but wrong date. "999999" becomes a day in 2007.
 * An unreadable expiration is worth discarding; a fabricated one would be recorded against a lot as
 * though someone had read it off the carton.
 */
function isEncodableExpiration(raw?: string, parsed?: Date): boolean {
    if (!raw || !parsed) return false;

    const month = parseInt(raw.slice(2, 4), 10);
    const day = parseInt(raw.slice(4, 6), 10);

    if (!(month >= 1 && month <= 12)) return false;
    if (!(day >= 0 && day <= 31)) return false;

    // A day past the end of its month rolls into the next one, so the round trip catches what the
    // range checks cannot - "270431" is six plausible digits and still not a date.
    return parsed.getMonth() === month - 1;
}

/**
 * Reads the drug fields out of a GS1 payload.
 *
 * The scan is passed through untouched: the parser reads the FNC1 field separator as the GS control
 * character scanners actually emit, so nothing has to be substituted on the way in.
 *
 * Throws whatever the parser throws for a payload holding no recognisable AI at all. A clipped read
 * is not reported that way though — it yields a short GTIN, or a field left null — so a caller that
 * needs to know a scan was good has to check the fields it actually wanted.
 */
export function parseGs1Barcode(barcode: string): Gs1ScanFields {
    const parsed = parseBarcode(barcode);
    const item = (ai: string): any => parsed.parsedCodeItems.find((i: any) => i.ai === ai);
    const expiration = item("17");

    return {
        gtin: item("01")?.data,
        lotNumber: item("10")?.data,
        serialNumber: item("21")?.data,
        expirationDate: isEncodableExpiration(expiration?.raw, expiration?.data)
            ? expiration.data
            : undefined,
    };
}
